import React, { useState, useRef } from 'react';
import { AttachButton } from 'components/molecules';
import { Button, TextArea } from 'components/atoms';
import { API_CONFIG } from 'config/api.config';
import useChatStore from 'stores/chatStore';

export interface ClassificationOptions {
  skipTariff?: boolean;
}

interface ChatInputProps {
  onSendMessage: (message: string, file?: File, options?: ClassificationOptions, additionalFiles?: File[]) => void;
  isProcessing?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isProcessing = false }) => {
  const [message, setMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const skipTariff = useChatStore((s) => s.skipTariff);
  const setSkipTariff = useChatStore((s) => s.setSkipTariff);
  const dragCounter = useRef(0);

  const handleFileSelect = (files: File[]) => {
    if (files && files.length > 0) {
      setAttachedFiles([...attachedFiles, ...files]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if ((attachedFiles.length > 0 || message.trim()) && onSendMessage) {
      const primaryFile = attachedFiles.length > 0 ? attachedFiles[0] : undefined;
      const additionalFiles = attachedFiles.length > 1 ? attachedFiles.slice(1) : undefined;
      const messageToSend = message.trim();

      if (messageToSend || primaryFile) {
        onSendMessage(messageToSend, primaryFile, { skipTariff }, additionalFiles);
        setMessage('');
        setAttachedFiles([]);
      }
    }
  };

  const removeFile = (indexToRemove: number): void => {
    setAttachedFiles(attachedFiles.filter((_, index) => index !== indexToRemove));
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isProcessing) {
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isProcessing) {
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current = 0;
    setIsDragging(false);

    if (isProcessing) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={handleSubmit}>
        <div
          className="relative bg-white border border-[#e0e0e0] rounded-[20px] overflow-hidden transition-all duration-200"
          style={{
            boxShadow: '0px 40px 48px -20px rgba(0,0,0,0.02), 0px 32px 32px -20px rgba(0,0,0,0.03), 0px 16px 24px -12px rgba(0,0,0,0.03), 0px 0px 0px 1px rgba(0,0,0,0.03)'
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDragging && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 pointer-events-none z-10">
              <div className="text-center">
                <div className="text-gray-700 text-lg font-medium mb-2">
                  Drop files here
                </div>
                <div className="text-gray-600 text-sm">
                  {API_CONFIG.FILE_UPLOAD.ACCEPTED_TYPES.join(', ')}
                </div>
              </div>
            </div>
          )}

          {/* Message Input Area */}
          <div className="p-4 h-16 flex items-start">
            <TextArea
              placeholder="Ask Classfi"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={1}
              className="w-full border-none outline-none resize-none text-[16px] leading-[1.3] text-gray-800 bg-transparent font-medium p-0 placeholder:text-[#979797]"
              disabled={isProcessing}
            />
          </div>

          {/* Show attached files */}
          {attachedFiles.length > 0 && (
            <div className="px-4 pb-3 space-y-2">
              {attachedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isProcessing}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Bottom Section with Border */}
          <div className="border-t border-[rgba(0,0,0,0.05)] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AttachButton
                onFileSelect={handleFileSelect}
                acceptedTypes={API_CONFIG.FILE_UPLOAD.ACCEPTED_TYPES.join(',')}
                helperText={`Support formats: ${API_CONFIG.FILE_UPLOAD.ACCEPTED_TYPES.join(', ')}`}
                multiple={true}
                className={isProcessing ? 'opacity-50 pointer-events-none' : ''}
              />
              <button
                type="button"
                onClick={() => setSkipTariff(!skipTariff)}
                disabled={isProcessing}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] transition-colors ${
                  skipTariff
                    ? 'text-amber-700 hover:bg-amber-50'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                title={skipTariff ? 'Tariff monitoring disabled — click to enable' : 'Click to skip tariff impact analysis'}
              >
                <span>{skipTariff ? 'Tariff off' : 'Tariff on'}</span>
              </button>
            </div>

            {isProcessing ? (
              <div className="flex items-center gap-2 text-sm text-teal-600">
                <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </div>
            ) : (
              <Button
                type="submit"
                variant="teal"
                className="p-[13px] rounded-full flex items-center justify-center"
                disabled={attachedFiles.length === 0 && !message.trim()}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 10L17 10M17 10L11 4M17 10L11 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>
            )}
          </div>
        </div>
      </form>

      <div className="text-center mt-3">
        <p className="text-xs text-gray-400">
          By uploading documents, you agree to our{' '}
          <a href="https://www.classfi.ai/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Terms</a>{' '}
          and have read our{' '}
          <a href="https://www.classfi.ai/privacypolicy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

export default ChatInput;
